import {
  getNativeBilling,
  storeProductId,
  storeLog,
  waitForNativeBilling,
  type StorePlatform,
} from "@/lib/store-billing";
import { linkStorePurchase, refreshStoreSubscriptions } from "@/lib/store-billing.functions";
import { track } from "@/lib/analytics";

export type StorePurchaseError =
  | "no_bridge"
  | "cancelled"
  | "pending"
  | "already_owned"
  | "product_unavailable"
  | "receipt_invalid"
  | "store_not_configured"
  | "failed";

export type StorePurchaseResult =
  | { ok: true; status: string | undefined; expiresAt: string | null | undefined; restored?: boolean }
  | { ok: false; error: StorePurchaseError };

/** Maps a raw store/plugin error message onto a coarse, loggable code. */
function classifyStoreError(message: string): StorePurchaseError {
  if (/cancel/i.test(message)) return "cancelled";
  if (/pending|deferred|ask.?to.?buy/i.test(message)) return "pending";
  if (/already|owned|duplicate/i.test(message)) return "already_owned";
  if (/product.*(not|un)available|no such product|not_found|product_not_found/i.test(message))
    return "product_unavailable";
  return "failed";
}

/**
 * Runs the platform purchase sheet inside the iOS/Android wrapper and links the
 * verified receipt to the signed-in account. Web visitors keep using Stripe.
 */
export async function purchasePremiumViaStore(
  plan: "monthly" | "yearly" = "monthly",
): Promise<StorePurchaseResult> {
  const bridge = getNativeBilling() ?? (await waitForNativeBilling());
  const productId = storeProductId(plan);
  if (!bridge) {
    storeLog("purchase_no_bridge", { product_id: productId });
    return { ok: false, error: "no_bridge" };
  }

  storeLog("purchase_initiated", { product_id: productId, plan });
  try {
    const purchase = await bridge.purchase(productId);
    storeLog("purchase_completed", { product_id: purchase.productId, platform: purchase.platform });
    void track("store_purchase_completed", { platform: purchase.platform, product_id: purchase.productId });
    const result = await linkStorePurchase({
      data: { platform: purchase.platform as StorePlatform, receipt: purchase.receipt, productId: purchase.productId },
    });
    storeLog("entitlement_verified", { ok: Boolean(result?.ok), status: result?.status ?? null });
    if (!result?.ok) return { ok: false, error: (result?.error as StorePurchaseError) ?? "failed" };
    return { ok: true, status: result.status, expiresAt: result.expiresAt };
  } catch (e) {
    const message = e instanceof Error ? e.message : String(e);
    const error = classifyStoreError(message);
    storeLog("purchase_failed", { product_id: productId, error });
    if (error === "cancelled") return { ok: false, error };
    if (error === "already_owned") {
      // Apple already owns this subscription — re-verify instead of failing.
      const restored = await restoreStorePurchases();
      if (restored.premium) return { ok: true, status: "active", expiresAt: null, restored: true };
      return { ok: false, error: "already_owned" };
    }
    if (error !== "pending") console.error("Store purchase failed:", message);
    return { ok: false, error };
  }
}

/**
 * Restores store entitlements: asks the OS, then re-verifies server-side.
 * `storeChecked` reports whether the StoreKit / Play step actually ran, so
 * callers can tell "no purchases on this Apple ID" apart from "the store was
 * never reached because the billing plugin is missing".
 */
export async function restoreStorePurchases(): Promise<{
  premium: boolean;
  refreshed: number;
  storeChecked: boolean;
  storePurchases: number;
}> {
  const bridge = getNativeBilling() ?? (await waitForNativeBilling());
  storeLog("restore_initiated", { has_bridge: Boolean(bridge) });
  let storeChecked = false;
  let storePurchases = 0;
  if (bridge?.restore) {
    try {
      const owned = await bridge.restore();
      storeChecked = true;
      storePurchases = owned.length;
      storeLog("restore_store_result", { count: owned.length });
      for (const purchase of owned) {
        await linkStorePurchase({
          data: { platform: purchase.platform, receipt: purchase.receipt, productId: purchase.productId },
        });
      }
    } catch (e) {
      storeLog("restore_failed", { error: e instanceof Error ? e.name : "unknown" });
      console.error("Store restore failed:", e);
    }
  }
  const result = await refreshStoreSubscriptions();
  storeLog("restore_completed", {
    premium: Boolean(result?.premium),
    refreshed: result?.refreshed ?? 0,
    store_checked: storeChecked,
    store_purchases: storePurchases,
  });
  return {
    premium: Boolean(result?.premium),
    refreshed: result?.refreshed ?? 0,
    storeChecked,
    storePurchases,
  };
}


/** Opens the OS subscription management screen when available. */
export async function openStoreManagement(): Promise<boolean> {
  const bridge = getNativeBilling() ?? (await waitForNativeBilling());
  if (!bridge?.manageSubscriptions) return false;
  try {
    await bridge.manageSubscriptions();
    return true;
  } catch {
    return false;
  }
}

/**
 * Asks StoreKit / Play Billing whether this customer may still receive the
 * introductory free trial for the monthly product. Returns `null` when the
 * store cannot tell us (older wrapper build) so callers fall back to the
 * server-side `trial_used` flag instead of inventing an eligibility answer.
 */
export async function storeIntroOfferEligibility(
  plan: "monthly" | "yearly" = "monthly",
): Promise<boolean | null> {
  const bridge = getNativeBilling() ?? (await waitForNativeBilling());
  if (!bridge?.introOfferEligibility) return null;
  try {
    return Boolean(await bridge.introOfferEligibility(storeProductId(plan)));
  } catch (e) {
    storeLog("intro_offer_check_failed", { plan });
    console.error("Intro offer eligibility check failed:", e);
    return null;
  }
}

/**
 * Localized store metadata (display price and any introductory offer) for a
 * plan. Returns `null` outside the wrapper or on older wrapper builds that do
 * not implement `productInfo`, so callers fall back to web copy.
 */
export async function storeProductInfo(plan: "monthly" | "yearly" = "monthly"): Promise<{
  productId: string;
  displayPrice?: string;
  introOffer?: { periodUnit?: string; periodCount?: number; isFreeTrial?: boolean } | null;
  storeExpiresAt?: string | null;
} | null> {
  const bridge = getNativeBilling() ?? (await waitForNativeBilling());
  if (!bridge?.productInfo) return null;
  const productId = storeProductId(plan);
  try {
    const info = (await bridge.productInfo(productId)) ?? null;
    storeLog("product_loaded", { product_id: productId, has_price: Boolean(info?.displayPrice) });
    return info;
  } catch (e) {
    storeLog("product_load_failed", { product_id: productId });
    console.error("Store product info lookup failed:", e);
    return null;
  }
}
